import React, { useCallback } from 'react'

import FourWheeledVehicleLine from 'components/elements/items/DamagedPartLine/FourWheeledVehicleLine'
import { AccidentPartInfo, VehiclePart } from 'states/QnState'
import { useUpdateLog } from 'utils/log'
import { FormTitleProps } from 'components/elements/items/FormTitle'
import MotorbikeLine from 'components/elements/items/DamagedPartLine/MoterbikeLine'
import CheckLine from 'components/elements/items/CheckLine'
import BaseGroup from '../BaseGroup'

/** 自動車の破損個所を入力するPropsのtype定義 */
type DivideVehicleGroupProps = FormTitleProps & {
  /** 破損箇所情報 */
  damagedStatus: AccidentPartInfo
  /** 対象がバイクかどうか */
  isMotorbike: boolean
  /** 破損タイヤの有無の文言 指定がない場合は表示しない */
  existsDamagedTireLabel?: string
  /** 破損個所の変更コールバック */
  onChanged: (value: AccidentPartInfo) => void
}

/**
 * 四輪/二輪の損傷箇所を入力する共通コンポーネント
 * @param props
 * @returns
 */
const SelectDamagedPartGroup: React.FC<DivideVehicleGroupProps> = (props) => {
  const {
    damagedStatus,
    isMotorbike,
    existsDamagedTireLabel,
    onChanged,
  } = props

  /** 損傷個所入力時に発火するコールバックイベント */
  const toggleDamagePart = useCallback(
    (partKey: VehiclePart) => {
      if (!onChanged) {
        /** コールバックイベント(onChanged)の指定がない場合は処理を抜ける */
        return
      }

      /** 選択状態の損傷個所の更新後のリスト */
      let updateDamageParts: Array<VehiclePart> = [
        ...damagedStatus.parts,
        partKey,
      ]

      /** 同じパーツを選択時に非活性状態にする */
      if (damagedStatus.parts.includes(partKey)) {
        /** 同一の損傷個所を選択時、該当の損傷個所を非活性状態にする */
        updateDamageParts = damagedStatus.parts.filter((part: VehiclePart) => {
          return part !== partKey
        })

        /** 全面/後面選択時に左右の活性状態を非活性状態に変更する */
        if (partKey === 'allFront' || partKey === 'allRear') {
          updateDamageParts = damagedStatus.parts.filter((part) => {
            return partKey === 'allFront'
              ? part !== 'leftFront' &&
                  part !== 'rightFront' &&
                  part !== partKey
              : part !== 'leftRear' && part !== 'rightRear' && part !== partKey
          })
        }

        /** 全面もしくは後面選択状態かつ、左右のいずれかを非チェック状態に変更した場合、全面もしくは後面を非活性にする */
        if (partKey === 'leftFront' || partKey === 'rightFront') {
          updateDamageParts = damagedStatus.parts.filter((part) => {
            return part !== 'allFront' && part !== partKey
          })
        }
        if (partKey === 'leftRear' || partKey === 'rightRear') {
          updateDamageParts = damagedStatus.parts.filter((part) => {
            return part !== 'allRear' && part !== partKey
          })
        }
      } else {
        /** 前面もしくは後面選択時、左右両方のチェックを活性状態にする */
        if (partKey === 'allFront' || partKey === 'allRear') {
          updateDamageParts =
            partKey === 'allFront'
              ? [...damagedStatus.parts, 'leftFront', 'rightFront', partKey]
              : [...damagedStatus.parts, 'leftRear', 'rightRear', partKey]
        }

        /** 左右の両方がチェック状態時、全面もしくは後面を活性状態にする */
        if (
          updateDamageParts.includes('leftFront') &&
          updateDamageParts.includes('rightFront')
        ) {
          updateDamageParts = [...updateDamageParts, 'allFront']
        }
        if (
          updateDamageParts.includes('leftRear') &&
          updateDamageParts.includes('rightRear')
        ) {
          updateDamageParts = [...updateDamageParts, 'allRear']
        }
      }

      onChanged({
        ...damagedStatus,
        parts: updateDamageParts,
      })
    },
    [onChanged]
  )
  const handleCheckClick = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!onChanged) {
        return
      }
      event &&
        onChanged({
          ...damagedStatus,
          existsDamagedTire: !damagedStatus.existsDamagedTire,
        })
    },
    [onChanged]
  )

  useUpdateLog(
    props.title,
    {
      title: props.title,
      isRequired: props.isRequired,
      damagedStatus: damagedStatus,
    },
    [props]
  )
  return (
    <BaseGroup
      isRequired={props.isRequired}
      title={props.title}
      description={props.description}
      hideTitle={props.hideTitle}
    >
      {isMotorbike ? (
        <MotorbikeLine
          selectedParts={damagedStatus.parts}
          toggleDamagedPart={toggleDamagePart}
        />
      ) : (
        <FourWheeledVehicleLine
          selectedParts={damagedStatus.parts}
          toggleDamagedPart={toggleDamagePart}
        />
      )}

      {existsDamagedTireLabel && (
        <CheckLine
          isChecked={damagedStatus.existsDamagedTire}
          label={existsDamagedTireLabel}
          onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
            handleCheckClick(event)
          }}
        />
      )}
    </BaseGroup>
  )
}

export default React.memo<DivideVehicleGroupProps>(SelectDamagedPartGroup)
